import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChevronDownIconComponent,
  ChevronRightIconComponent,
  SearchIconComponent,
  WorkflowIconComponent,
} from '@kuiper/icons';
import tippy, { Instance, Placement, Props } from 'tippy.js';
import { debounce, map, of, timer } from 'rxjs';
import { ChildFlow } from '../../../core/common/models/child-flow.model';
import { StepStatusEnum } from '../../../core/common/enums/step-status';
import { BadgeComponent } from '../badge/badge.component';
import { TrimTooltipDirective } from '../../directives/trim-tooltip/trim-tooltip.directive';

interface RelatedRequestListItem {
  id: number;
  displayName: string;
  stateLabel?: string;
  statusBadgeClass: string;
  viewLink: string | (string | number)[];
}

const RELATED_REQUESTS_TOGGLE_SELECTOR =
  'button[name="relatedRequestsToggle"]';

@Component({
  selector: 'lib-related-requests-dropdown',
  imports: [
    TranslocoPipe,
    RouterLink,
    ReactiveFormsModule,
    BadgeComponent,
    TrimTooltipDirective,
    ChevronDownIconComponent,
    ChevronRightIconComponent,
    SearchIconComponent,
    WorkflowIconComponent,
  ],
  templateUrl: './related-requests-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatedRequestsDropdownComponent implements OnDestroy {
  /** Ensures only one related-requests menu is open across table rows. */
  private static openMenu: RelatedRequestsDropdownComponent | null = null;

  readonly childFlows = input<ChildFlow[] | null | undefined>([]);
  readonly viewDetailsRouterLink = input.required<
    (childFlow: ChildFlow) => string | (string | number)[]
  >();

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  readonly searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(
      map(value => value.trim().toLowerCase()),
      debounce(value => (value === '' ? of(0) : timer(305)))
    ),
    { initialValue: this.searchControl.value.trim().toLowerCase() }
  );

  readonly isMenuOpen = signal(false);

  readonly relatedRequestItems = computed(() => {
    const flows = this.childFlows() ?? [];
    const resolveLink = this.viewDetailsRouterLink();
    return flows.map(flow => this.toListItem(flow, resolveLink));
  });

  readonly filteredChildFlows = computed(() => {
    const term = this.searchTerm();
    const items = this.relatedRequestItems();

    if (!term) {
      return items;
    }

    return items.filter(item =>
      item.displayName.toLowerCase().includes(term)
    );
  });

  readonly relatedCount = computed(() => this.childFlows()?.length ?? 0);
  readonly hasRelatedRequests = computed(() => this.relatedCount() > 0);

  private tippyMenuInstance: Instance<Props> | null = null;
  private currentTippyTrigger: HTMLElement | null = null;

  private readonly menuOverlayContent =
    viewChild<ElementRef<HTMLDivElement>>('menuOverlayContent');

  toggleMenu(event: MouseEvent): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const triggerElement = event.currentTarget as HTMLElement | null;
    if (!triggerElement || !this.hasRelatedRequests()) {
      return;
    }

    this.ensureTippyMenu(triggerElement);
    if (!this.tippyMenuInstance) {
      return;
    }

    if (this.isMenuOpen()) {
      this.hideMenu();
      return;
    }

    RelatedRequestsDropdownComponent.openMenu?.hideMenu();
    RelatedRequestsDropdownComponent.openMenu = this;
    this.tippyMenuInstance.show();
  }

  private hideMenu(): void {
    if (!this.tippyMenuInstance) {
      return;
    }

    // Instant hide avoids overlapping open/close animations when switching rows.
    this.tippyMenuInstance.setProps({ duration: 0 });
    this.tippyMenuInstance.hide();

    if (RelatedRequestsDropdownComponent.openMenu === this) {
      RelatedRequestsDropdownComponent.openMenu = null;
    }
  }

  private toListItem(
    flow: ChildFlow,
    resolveLink: (childFlow: ChildFlow) => string | (string | number)[]
  ): RelatedRequestListItem {
    return {
      id: flow.id,
      displayName: flow.request_subject ?? '',
      stateLabel: flow.state.label,
      statusBadgeClass: this.resolveStatusBadgeClass(flow.state.value),
      viewLink: resolveLink(flow),
    };
  }

  private resolveStatusBadgeClass(stateValue: string | undefined): string {
    switch (stateValue) {
      case StepStatusEnum.COMPLETED:
        return 'bg-success-50 text-success-700';
      case StepStatusEnum.INPROGRESS:
      case StepStatusEnum.PENDING:
      case StepStatusEnum.EDITED:
        return 'bg-warning-50 text-warning-700';
      case StepStatusEnum.REJECTED:
      case StepStatusEnum.SENT_FORWARD:
      case StepStatusEnum.SENT_BACK:
      case StepStatusEnum.CANCELLED:
        return 'bg-destructive-50 text-destructive-700';
      case StepStatusEnum.SCHEDULED:
        return 'bg-primary-50 text-primary-700';
      case StepStatusEnum.ON_HOLD:
        return 'bg-neutral-100 text-neutral-800';
      default:
        return 'bg-neutral-50 text-neutral-700';
    }
  }

  private ensureTippyMenu(triggerElement: HTMLElement): void {
    const content = this.menuOverlayContent();
    if (!content) {
      return;
    }

    if (
      this.tippyMenuInstance &&
      this.currentTippyTrigger &&
      this.currentTippyTrigger !== triggerElement
    ) {
      this.tippyMenuInstance.destroy();
      this.tippyMenuInstance = null;
    }

    if (this.tippyMenuInstance) {
      return;
    }

    content.nativeElement.classList.remove('hidden');
    this.currentTippyTrigger = triggerElement;

    const preferredPlacement: Placement = 'bottom-start';
    const fallbackPlacement: Placement = 'top-start';

    this.tippyMenuInstance = tippy(triggerElement, {
      content: content.nativeElement,
      interactive: true,
      trigger: 'manual',
      hideOnClick: false,
      appendTo: () => document.body,
      zIndex: 9999,
      placement: preferredPlacement,
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: [fallbackPlacement],
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              rootBoundary: 'viewport',
              padding: 8,
              altAxis: true,
            },
          },
        ],
      },
      duration: [200, 0],
      onShow: () => {
        this.isMenuOpen.set(true);
        RelatedRequestsDropdownComponent.openMenu = this;
      },
      onHidden: () => {
        this.isMenuOpen.set(false);
        this.resetSearchState();
        this.tippyMenuInstance?.setProps({ duration: [200, 0] });
        if (RelatedRequestsDropdownComponent.openMenu === this) {
          RelatedRequestsDropdownComponent.openMenu = null;
        }
      },
      onClickOutside: (_instance, event) => {
        const target = event.target as Element | null;
        // Another row's toggle owns open/close; avoid racing hide animations.
        if (target?.closest(RELATED_REQUESTS_TOGGLE_SELECTOR)) {
          return;
        }
        this.hideMenu();
      },
      onMount: instance => {
        const tippyBox = instance.popper.querySelector(
          '.tippy-box'
        ) as HTMLElement | null;
        const tippyContent = instance.popper.querySelector(
          '.tippy-content'
        ) as HTMLElement | null;

        if (tippyBox) {
          tippyBox.dataset['menuTippy'] = 'true';
          tippyBox.style.overflow = 'visible';
        }
        if (tippyContent) {
          tippyContent.style.overflow = 'visible';
        }
      },
    });
  }

  private resetSearchState(): void {
    this.searchControl.setValue('');
  }

  ngOnDestroy(): void {
    if (RelatedRequestsDropdownComponent.openMenu === this) {
      RelatedRequestsDropdownComponent.openMenu = null;
    }
    this.tippyMenuInstance?.destroy();
  }
}
